<template>
    <div class="vue-funnel-header">
        <section class="vue-left-controls">
            <div class="top-row">
                <DsIconButton
                    class="close-automation-button"
                    name="x"
                    @click="$emit('save-and-close')"
                />

                <div data-qa="funnel-name" class="funnel-name">
                    {{ funnelName }}
                </div>
            </div>

            <div class="funnel-subheader">
                <div data-qa="funnel-categories" class="funnel-categories">
                    {{ funnelCategories }}
                </div>

                <AutomationFunnelStatus
                    :funnel-status="funnelStatus"
                    @open-unpublish-modal="openUnpublishModal"
                    @open-publish-modal="openPublishModal"
                    @delete-automation="openDeleteCampaignModal"
                    @discard-changes="revertChanges"
                />
            </div>
        </section>

        <section class="vue-right-controls">
            <DsIcon
                v-if="lastSavedLabel"
                data-qa="last-saved-timestamp"
                name="check-circle"
                class="save-timestamp-icon"
            />

            <div v-if="lastSavedLabel" class="save-timestamp">
                {{ lastSavedLabel }}
            </div>

            <SaveStatus v-else data-qa="save-status" :status="saveStatus" />

            <div v-if="flagsInfo.bigCardInlineAdd" class="feature-toggle-wrapper">
                <DsToggleSwitch
                    :value="betaFeaturesEnabled"
                    :label-on="$t('funnel.header.automation.features.label')"
                    :label-off="$t('funnel.header.automation.features.label')"
                    class="automation-features-toggle"
                    @input="toggleBetaFeatures"
                />
            </div>

            <HeaderStealthMenu
                v-if="showStealthMenu"
                data-qa="stealth-menu-button"
                @click-export="onClickExport"
                @click-import="onClickImport"
                @click-reset="onClickReset"
            />

            <div class="campaign-toggle reporting-toggle" @click="showReportingViewHandler">
                <DsTooltip wrap :disabled="!showLockedAutomationToolTip" class="automation-lock-tooltip">
                    <template #reference>
                        <DsTextButton
                            v-if="showReportingView"
                            data-qa="editor-view-button"
                            leading-icon="edit"
                            :disabled="locked"
                            :class="{'disabled-edit-button': locked }"
                        >
                            {{ $t('funnel.header.reporting.exit') }}
                        </DsTextButton>

                        <DsTextButton
                            v-else
                            data-qa="reporting-view-button"
                            leading-icon="bar-chart-2"
                            :disabled="locked"
                            :class="{'disabled-edit-button': locked }"
                        >
                            {{ $t('funnel.header.reporting') }}
                        </DsTextButton>
                    </template>

                    <template #default>
                        <div>{{ $t('funnel.header.locked.tool.tip.text') }}</div>
                    </template>
                </DsTooltip>
            </div>

            <div class="action-dropdown">
                <HeaderActionsMenu
                    :can-lock-automation="canLockAutomation"
                    :can-copy-via-content-publishing="canCopyViaContentPublishing"
                    :can-publish-to-marketplace="canPublishToMarketplace"
                    :hide-dashboard-widget-option="hideDashboardWidgetOption"
                    :locked="locked"
                    :is-published="published"
                    @action-clicked="onClickActionMenuItem"
                />
            </div>

            <div v-if="!showReportingView">
                <DsFilledButton data-qa="publish-button" @click="openPublishModal">
                    {{ $t('funnel.header.publish') }}
                </DsFilledButton>
            </div>
        </section>

        <CreateDashboardWidgetModal
            v-if="showCreateDashboardWidgetModal"
            :is-open="showCreateDashboardWidgetModal"
            :funnel-id="funnelId"
            :is-published="published"
            :published-xml="publishedXml"
            @close-create-dashboard-widget="closeCreateDashboardWidgetModal"
        />

        <RestoreVersionModal
            v-if="showRestoreVersionModal"
            :is-open="showRestoreVersionModal"
            :funnel-id="funnelId"
            @restore-version="restoreVersion"
            @close-restore-version="closeRestoreVersionModal"
        />

        <MakeCopyModal
            v-if="showMakeCopyModal"
            :is-open="showMakeCopyModal"
            :funnel-id="funnelId"
            :funnel-name="funnelName"
            :draft-json="draftJson"
            @close-make-copy="closeMakeCopyModal"
        />

        <MergeFieldsModal
            v-if="showMergeFieldsModal"
            :is-open="showMergeFieldsModal"
            :funnel-id="funnelId"
            :draft-json="draftJson"
            @mark-dirty="markDirty"
            @close-merge-fields="closeMergeFieldsModal"
        />

        <LinksModal
            v-if="showLinksModal"
            :is-open="showLinksModal"
            :funnel-id="funnelId"
            :draft-json="draftJson"
            @mark-dirty="markDirty"
            @close-campaign-links="closeLinksModal"
        />

        <CopyViaContentPublishingModal
            v-if="showCopyViaContentPublishingModal"
            :is-open="showCopyViaContentPublishingModal"
            :funnel-id="funnelId"
            :funnel-name="funnelName"
            @save-automation="saveAutomation"
            @close-copy-publishing="closeCopyViaContentPublishingModal"
        />

        <EditNameAndCategory
            v-if="showEditNameCategoriesModal"
            :funnel-id="funnelId"
            :funnel-name="funnelName"
            :assigned-categories="assignedCategories"
            :all-categories="allCategories"
            @update-name="updateFunnelName"
            @update-categories="updateFunnelCategories"
            @close-name-and-category="closeEditNameCategoriesModal"
            @refresh-automation-categories="refreshAutomationCategories"
        />

        <LockUnlockAutomationModal
            v-if="showLockUnlockModal"
            :is-open="showLockUnlockModal"
            :campaign-locked="locked"
            :funnel-id="funnelId"
            @close-lock-unlock-campaign-modal="closeLockUnlockAutomationModal"
            @save-campaign="saveAutomation"
            @campaign-lock-updated="updateLock"
        />

        <UnpublishModal
            v-if="showUnpublishModal"
            :draft-json="draftJson"
            @unpublish="unpublish"
            @close-unpublish="closeUnpublishModal"
        />

        <PublishModal
            v-if="showPublish"
            :funnel-id="funnelId"
            :funnel-draft-xml="funnelDraftXml"
            @verify-and-publish="verifyAndPublish"
            @close-publish="closePublishModal"
            @open-cell-modal-by-id="openCellModalById"
        />
        <DeleteAutomationModal
            v-if="showDeleteAutomationModal"
            :is-open="showDeleteAutomationModal"
            :funnel-id="funnelId"
            @navigate-back="$emit('close-automation-builder')"
            @close-delete-campaign-modal="closeDeleteCampaignModal"
        />

        <PublishMarketplaceModal
            v-if="showPublishMarketplaceModal"
            :is-open="showPublishMarketplaceModal"
            :funnel-id="funnelId"
            :funnel-name="funnelName"
            @save-campaign="saveAutomation"
            @close-publish-marketplace="closePublishMarketplaceModal"
        />

        <ExportXmlModal
            v-if="showExportXmlModal"
            :is-open="showExportXmlModal"
            :funnel-id="funnelId"
            :current-funnel-draft-xml="funnelDraftXml"
            :original-funnel-draft-xml="originalFunnelDraftXml"
            :original-funnel-published-xml="publishedXml"
            @close-export-xml="closeExportXmlModal"
        />

        <ImportXmlModal
            v-if="showImportXmlModal"
            :is-open="showImportXmlModal"
            :funnel-id="funnelId"
            @import-xml="importXmlToBuilder"
            @close-import-xml="closeImportXmlModal"
        />

        <ItemScreenDisplayModal
            v-if="showConfigItemScreenModal"
            :is-open="showConfigItemScreenModal"
            :funnel-id="funnelId"
            :auto-open-config-screen="autoOpenConfigScreen"
            @update-open-automatically-header="updateOpenAutomaticallyHeader"
            @close-item-screen-modal="closeConfigItemScreenModal"
        />
    </div>
</template>

<script>
import HeaderActionsMenu from './HeaderActionsMenu.vue';
import HeaderStealthMenu from './HeaderStealthMenu.vue';
import AutomationFunnelStatus from './AutomationFunnelStatus.vue';
import CreateDashboardWidgetModal from '../configuration/createDashboardWidget/CreateDashboardWidget.vue';
import RestoreVersionModal from '../configuration/restoreVersion/RestoreVersion.vue';
import MakeCopyModal from '../configuration/makeCopy/MakeCopy.vue';
import MergeFieldsModal from '../configuration/mergefields/MergeFields.vue';
import LinksModal from '../configuration/links/LinksModal.vue';
import CopyViaContentPublishingModal from '../configuration/copyViaContentPublishing/CopyViaContentPublishing.vue';
import EditNameAndCategory from '../configuration/editNameAndCategory/EditNameAndCategory.vue';
import UnpublishModal from '../configuration/unpublish/UnpublishModal.vue';
import PublishModal from '../configuration/publish/PublishModal.vue';
import DeleteAutomationModal from '../configuration/deleteAutomation/DeleteAutomation.vue';
import PublishMarketplaceModal from '../configuration/publishMarketplace/PublishMarketplace.vue';
import LockUnlockAutomationModal from '../configuration/locking/LockUnlockAutomation.vue';
import ExportXmlModal from '../configuration/exportXml/ExportXml.vue';
import ImportXmlModal from '../configuration/importXml/ImportXml.vue';
import ItemScreenDisplayModal from '../configuration/itemScreen/ItemScreenDisplayModal.vue';
import {
    OPTION_CONSTANTS,
    CONSTANTS_STEALTH_MENU,
    AUTOMATION_STATUS,
    SAVE_STATUS,
} from '../utils/editor.constants';
import SaveStatus from './SaveStatus.vue';


export default {
    OPTION_CONSTANTS,
    CONSTANTS_STEALTH_MENU,

    components: {
        AutomationFunnelStatus,
        HeaderActionsMenu,
        HeaderStealthMenu,
        CreateDashboardWidgetModal,
        MakeCopyModal,
        MergeFieldsModal,
        LinksModal,
        RestoreVersionModal,
        CopyViaContentPublishingModal,
        EditNameAndCategory,
        UnpublishModal,
        PublishModal,
        DeleteAutomationModal,
        LockUnlockAutomationModal,
        PublishMarketplaceModal,
        SaveStatus,
        ExportXmlModal,
        ImportXmlModal,
        ItemScreenDisplayModal,
    },

    props: {
        categoriesLoaded: {
            type: Boolean,
            default: false,
        },
        funnelStatus: {
            type: String,
            default: AUTOMATION_STATUS.DRAFT,
        },
        allCategories: {
            type: Array,
            required: true,
        },
        assignedCategories: {
            type: Array,
            required: true,
        },
        categories: {
            type: Array,
            default: () => [],
        },
        showReportingView: {
            type: Boolean,
            default: false,
        },
        saveStatus: {
            type: String,
            default: SAVE_STATUS.NONE,
        },
        lastSavedTimestamp: {
            type: Number,
            default: null,
        },
        builderInfo: {
            type: Object,
            required: true,
        },
        funnelInfo: {
            type: Object,
            required: true,
        },
        appInfo: {
            type: Object,
            required: true,
        },
        showPublish: {
            type: Boolean,
            default: false,
        },
        funnelDraftXml: {
            type: String,
            default: '',
        },
        autoOpenConfigScreen: {
            type: Boolean,
            required: true,
        },
        draftJson: {
            type: Object,
            default: () => ({}),
        },
        flagsInfo: {
            type: Object,
            default: () => ({}),
        },
        betaFeaturesEnabled: {
            type: Boolean,
            required: true,
        },
    },

    emits: [
        'close-automation-builder',
        'import-xml',
        'mark-dirty',
        'open-cell-modal-by-id',
        'refresh-automation-categories',
        'reporting-view-clicked',
        'restore-version',
        'revert-changes',
        'save-and-close',
        'save-campaign',
        'save-version',
        'show-publish-modal',
        'toggle-beta-features',
        'toggle-campaign-lock',
        'unlock-automation-status',
        'unpublish-campaign',
        'update-categories',
        'update-name',
        'update-open-automatically-campaign',
        'verify-and-publish',
    ],

    data() {
        return {
            showMergeFieldsModal: false,
            showLinksModal: false,
            showCreateDashboardWidgetModal: false,
            showRestoreVersionModal: false,
            showMakeCopyModal: false,
            showEditNameCategoriesModal: false,
            showCopyViaContentPublishingModal: false,
            showLockUnlockModal: false,
            showConfigItemScreenModal: false,
            showUnpublishModal: false,
            showDeleteAutomationModal: false,
            showPublishMarketplaceModal: false,
            showExportXmlModal: false,
            showImportXmlModal: false,
        };
    },

    computed: {
        funnelCategories() {
            if (!this.categoriesLoaded) return '...';

            if (!this.assignedCategories?.length) return this.$t('funnel.header.categories.none');

            return this.assignedCategories.map(({ label }) => label).join(', ');
        },

        showStealthMenu() {
            return !this.showReportingView && this.appInfo?.appEditionFlags?.local;
        },

        funnelId() {
            return this.funnelInfo.funnelId || 0;
        },

        funnelName() {
            return this.funnelInfo.funnelName;
        },

        originalFunnelDraftXml() {
            return this.funnelInfo.draftXml;
        },

        publishedXml() {
            return this.funnelInfo.publishedXml;
        },

        canPublishToMarketplace() {
            return this.builderInfo.canPublishToMarketplace;
        },

        hideDashboardWidgetOption() {
            return this.builderInfo.hideDashboardWidgetOption;
        },

        canCopyViaContentPublishing() {
            return this.builderInfo.allowCopyViaContentPublishing;
        },

        canLockAutomation() {
            return this.funnelInfo.canBeLocked;
        },

        locked() {
            return this.funnelInfo.locked;
        },
        published() {
            return this.funnelInfo.published;
        },

        lastSavedLabel() {
            if (!this.lastSavedTimestamp || this.saveStatus !== SAVE_STATUS.SUCCESS) return null;

            const dateString = new Date(this.lastSavedTimestamp)
                .toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
                .replace(' ', '')
                .toLowerCase();

            return `${this.$t('funnel.header.saved.at')} ${dateString}`;
        },

        showLockedAutomationToolTip() {
            return this.showReportingView && this.locked;
        },

    },
    mounted() {
        this.handleRenameModal();
    },

    methods: {
        async onClickActionMenuItem(optionId) {
            switch (optionId) {
            case OPTION_CONSTANTS.funnelWidget:
                await this.openCreateDashboardWidgetModal();

                return;
            case OPTION_CONSTANTS.mergeFields:
                this.openMergeFieldsModal();

                return;
            case OPTION_CONSTANTS.links:
                this.openLinksModal();

                return;
            case OPTION_CONSTANTS.restoreVersion:
                this.openRestoreVersionModal();

                return;
            case OPTION_CONSTANTS.makeCopy:
                this.openMakeCopyModal();

                return;
            case OPTION_CONSTANTS.copyViaContentPublishing:
                this.openCopyViaContentPublishingModal();

                return;
            case OPTION_CONSTANTS.rename:
            case OPTION_CONSTANTS.renameAndEditCategory:
                this.openEditNameCategoriesModal();

                return;
            case OPTION_CONSTANTS.unpublish:
                this.openUnpublishModal();

                return;
            case OPTION_CONSTANTS.deleteCampaign:
                this.openDeleteCampaignModal();

                return;
            case OPTION_CONSTANTS.publishMarketplace:
                this.openPublishMarketplaceModal();

                return;
            case OPTION_CONSTANTS.lock:
            case OPTION_CONSTANTS.unlock:
            case OPTION_CONSTANTS.lockUnlock:
                this.openLockUnlockCampaignModal();

                return;

            case OPTION_CONSTANTS.configItemScreen:
                this.openConfigItemScreenModal();

                return;

            case OPTION_CONSTANTS.revision:
                this.$emit('save-version');

                return;
            case OPTION_CONSTANTS.revert:
                this.revertChanges();

                return;
            default:
                this.$toast({ message: this.$t('funnel.header.error.invalidOption') });
            }
        },

        onClickExport() {
            this.openExportXmlModal();
        },

        onClickImport() {
            this.openImportXmlModal();
        },

        onClickReset() {
            this.$toast({ message: this.$t('funnel.header.error.invalidOption') });
        },

        handleRenameModal() {
            if (this.funnelInfo.locked) return;

            this.showEditNameCategoriesModal = !this.funnelInfo.funnelName
                                            || this.funnelInfo.funnelName === this.$t('funnel.name.unknown');
        },

        showReportingViewHandler() {
            if (!this.locked) {
                this.$emit('reporting-view-clicked');
            }
        },

        openPublishModal() {
            this.$emit('show-publish-modal', true);
        },

        revertChanges() {
            this.$emit('revert-changes');
        },

        openMakeCopyModal() {
            this.saveAutomation();
            this.showMakeCopyModal = true;
        },

        closeMakeCopyModal(unlockEditor = false) {
            this.showMakeCopyModal = false;

            if (unlockEditor) this.$emit('unlock-automation-status');
        },

        openMergeFieldsModal() {
            this.showMergeFieldsModal = true;
        },

        closeMergeFieldsModal() {
            this.showMergeFieldsModal = false;
        },

        openLinksModal() {
            this.showLinksModal = true;
        },

        markDirty() {
            this.$emit('mark-dirty');
        },

        closeLinksModal() {
            this.showLinksModal = false;
        },

        openCopyViaContentPublishingModal() {
            this.showCopyViaContentPublishingModal = true;
        },

        closeCopyViaContentPublishingModal() {
            this.showCopyViaContentPublishingModal = false;
        },

        openCreateDashboardWidgetModal() {
            this.showCreateDashboardWidgetModal = true;
        },

        openRestoreVersionModal() {
            this.saveAutomation();
            this.showRestoreVersionModal = true;
        },

        closeCreateDashboardWidgetModal() {
            this.showCreateDashboardWidgetModal = false;
        },

        closeRestoreVersionModal() {
            this.showRestoreVersionModal = false;
        },

        openEditNameCategoriesModal() {
            this.showEditNameCategoriesModal = true;
        },

        closeEditNameCategoriesModal() {
            this.showEditNameCategoriesModal = false;
        },

        openLockUnlockCampaignModal() {
            this.showLockUnlockModal = true;
        },

        openConfigItemScreenModal() {
            this.showConfigItemScreenModal = true;
        },

        closeConfigItemScreenModal() {
            this.showConfigItemScreenModal = false;
        },

        closeLockUnlockAutomationModal() {
            this.showLockUnlockModal = false;
        },

        openUnpublishModal() {
            this.showUnpublishModal = true;
        },

        closeUnpublishModal() {
            this.showUnpublishModal = false;
        },

        closePublishModal() {
            this.$emit('show-publish-modal', false);
        },

        openPublishMarketplaceModal() {
            this.showPublishMarketplaceModal = true;
        },

        closePublishMarketplaceModal() {
            this.showPublishMarketplaceModal = false;
        },

        openDeleteCampaignModal() {
            this.showDeleteAutomationModal = true;
        },

        closeDeleteCampaignModal() {
            this.showDeleteAutomationModal = false;
        },

        openExportXmlModal() {
            this.saveAutomation();
            this.showExportXmlModal = true;
        },

        closeExportXmlModal() {
            this.showExportXmlModal = false;
        },

        openImportXmlModal() {
            this.showImportXmlModal = true;
        },

        closeImportXmlModal() {
            this.showImportXmlModal = false;
        },

        updateFunnelName(newFunnelName) {
            this.$emit('update-name', newFunnelName);
        },

        updateFunnelCategories(categoriesSelected) {
            this.$emit('update-categories', categoriesSelected);
        },

        async updateLock(locked) {
            this.$emit('toggle-campaign-lock', locked);
        },

        unpublish() {
            this.$emit('unpublish-campaign');
            this.showUnpublishModal = false;
        },

        verifyAndPublish(event) {
            this.$emit('verify-and-publish', event);
        },

        openCellModalById(event) {
            this.$emit('open-cell-modal-by-id', event);
        },

        updateOpenAutomaticallyHeader(isAutomatically) {
            this.$emit('update-open-automatically-campaign', isAutomatically);
        },

        importXmlToBuilder(event) {
            this.$emit('import-xml', event);
            this.closeImportXmlModal();
        },

        restoreVersion(event) {
            this.$emit('restore-version', event);
            this.closeRestoreVersionModal();
        },

        saveAutomation() {
            this.$emit('save-campaign');
        },

        refreshAutomationCategories() {
            this.$emit('refresh-automation-categories');
        },

        toggleBetaFeatures() {
            this.$emit('toggle-beta-features');
        },

    },
};
</script>

<style lang="scss" scoped>
svg.save-timestamp-icon {
    --icon-size: 1rem;
    color: $color-green;
}

.vue-funnel-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: nowrap;
    height: 100%;

    .vue-funnel-title-container {
        font-family: Sul Sans;
        font-size: $font-size-lg;
        font-weight: 300;
        line-height: $line-height-lg;
        letter-spacing: 0px;
        text-align: left;
        margin-left: $spacing-200;
    }
}

.vue-left-controls {
    display: block;
    width: fit-content;

    .top-row {
        display: flex;
        grid-gap: $spacing-050;
        height: px-to-rem(24px);
        overflow: hidden;
        max-width: calc(100vw - 400px);

        .close-automation-button {
            padding: 0 $spacing-200;
        }

        .funnel-name {
            text-align: left;
            font-weight: 400;
            font-size: $font-size-lg;
            line-height: $line-height-lg;
            text-wrap: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            color: $color-ink-900;
        }
    }

    .funnel-subheader {
        display: flex;
        margin-left: px-to-rem(64px);
        grid-gap: $spacing-100;

        .funnel-categories {
            font-size: $font-size-sm;
            line-height: $line-height-md;
            text-wrap: nowrap;
        }
    }
}

.vue-right-controls {
    display: flex;
    width: fit-content;
    height: 40px;
    margin: $spacing-150;
    align-items: center;
    justify-content: flex-end;
    grid-gap: $spacing-100;

    .save-timestamp {
        color: $color-green;
        font-size: $font-size-sm;
        margin-right: $spacing-125;
    }

    .campaign-toggle {
        font-family: "Proxima Nova",sans-serif;
    }

    label.toggle-switch.automation-features-toggle {
        padding-top: 0;
    }

    .feature-toggle-wrapper {
        display: contents;
    }
}
</style>
